import { useQuery } from '@tanstack/react-query';
import { adminApi, type Stats, type EventLog } from '@/lib/api';
import { Users, Server, Box, DollarSign, Activity, ChevronRight, RefreshCw } from 'lucide-react';
import { formatDate, cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';

export function Dashboard() {
  const { data: stats, isLoading: statsLoading, refetch } = useQuery({
    queryKey: ['stats'],
    queryFn: adminApi.getStats,
    refetchInterval: 30000,
  });

  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ['events', { limit: 5 }],
    queryFn: () => adminApi.getEvents({ limit: 5 }),
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-foreground" />
          <span className="text-sm text-muted-foreground">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with pull-to-refresh style */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Your ClawdBot Factory overview</p>
        </div>
        <button 
          onClick={() => refetch()} 
          className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all"
        >
          <RefreshCw className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>
      
      {/* Stats Cards - Single column on mobile */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Customers"
          value={stats?.customers.total || 0}
          subtitle={`${stats?.customers.active || 0} active`}
          icon={Users}
          href="/customers"
        />
        <StatCard
          title="Instances"
          value={stats?.instances.running || 0}
          subtitle={`of ${stats?.instances.total || 0} running`}
          icon={Box}
          href="/instances"
          status={stats?.instances.running && stats.instances.running > 0 ? 'running' : undefined}
        />
        <StatCard
          title="Servers"
          value={stats?.servers.active || 0}
          subtitle={`of ${stats?.servers.total || 0} active`}
          icon={Server}
          href="/servers"
          status={stats?.servers.active && stats.servers.active > 0 ? 'running' : undefined}
        />
        <StatCard
          title="Revenue"
          value={`$${stats?.revenue.mrr || 0}`}
          subtitle={`${stats?.revenue.recentPayments || 0} this month`}
          icon={DollarSign}
        />
      </div>

      {/* Quick Stats Bar */}
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        <QuickStat 
          label="Running" 
          value={stats?.instances.running || 0} 
          status="running" 
        />
        <QuickStat 
          label="Stopped" 
          value={stats?.instances.stopped || 0} 
          status="stopped" 
        />
        <QuickStat 
          label="Errors" 
          value={(stats?.instances.total || 0) - (stats?.instances.running || 0) - (stats?.instances.stopped || 0)} 
          status="error" 
        />
      </div>

      {/* Recent Events */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Recent Activity
          </h2>
          <Link 
            to="/events" 
            className="text-sm text-muted-foreground flex items-center gap-1 active:opacity-70 transition-opacity"
          >
            View all
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        
        {eventsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
          </div>
        ) : events?.events.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Activity className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>No recent activity</p>
          </div>
        ) : (
          <div className="space-y-2">
            {events?.events.map((event: EventLog) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ElementType;
  href?: string;
  status?: 'running' | 'stopped' | 'error';
}

function StatCard({ title, value, subtitle, icon: Icon, href, status }: StatCardProps) {
  const content = (
    <div className={cn(
      "stat-card-mobile flex items-center gap-4 group",
      href && "active:scale-[0.98] active:bg-card/50"
    )}>
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted/50 shrink-0">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-semibold tracking-tight">{value}</span>
          {status && <div className={`status-dot status-dot-${status}`} />}
        </div>
        <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
      </div>
      {href && (
        <ChevronRight className="h-5 w-5 text-muted-foreground/50 group-active:translate-x-0.5 transition-transform shrink-0" />
      )}
    </div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }
  return content;
}

interface QuickStatProps {
  label: string;
  value: number;
  status: 'running' | 'stopped' | 'error';
}

function QuickStat({ label, value, status }: QuickStatProps) {
  const colors = {
    running: 'bg-status-success/30 border-status-success text-status-success-foreground',
    stopped: 'bg-secondary border-border text-muted-foreground',
    error: 'bg-status-error/30 border-status-error text-status-error-foreground',
  };

  return (
    <div className={cn(
      "flex items-center gap-2 px-4 py-2.5 rounded-full border whitespace-nowrap",
      colors[status]
    )}>
      <div className={`status-dot status-dot-${status}`} />
      <span className="text-sm font-medium">{value}</span>
      <span className="text-xs opacity-70">{label}</span>
    </div>
  );
}

function EventCard({ event }: { event: EventLog }) {
  const getEventStyle = (eventName: string) => {
    if (eventName.includes('error') || eventName.includes('failed')) {
      return { dot: 'status-dot-error', bg: 'bg-status-error/10' };
    }
    if (eventName.includes('started') || eventName.includes('activated') || eventName.includes('created')) {
      return { dot: 'status-dot-running', bg: 'bg-status-success/10' };
    }
    if (eventName.includes('stopped') || eventName.includes('cancelled')) {
      return { dot: 'status-dot-pending', bg: 'bg-status-warning/10' };
    }
    return { dot: 'status-dot-stopped', bg: 'bg-secondary/50' };
  };

  const style = getEventStyle(event.event);
  const formattedEvent = event.event
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <div className={cn(
      "mobile-card flex items-center gap-3",
      style.bg
    )}>
      <div className={cn("status-dot shrink-0", style.dot)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{formattedEvent}</p>
        <p className="text-xs text-muted-foreground truncate">
          {event.customerId ? `Customer ${event.customerId.slice(-8)}` : 'System'}
        </p>
      </div>
      <span className="text-xs text-muted-foreground shrink-0">
        {formatDate(event.createdAt)}
      </span>
    </div>
  );
}
